import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, FolderKanban, Inbox } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useSEO } from "../lib/seo";
import { PROJECT_FILTERS } from "../data/content";

const INPUT = "w-full border border-black/15 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-bati-sky";
const CATEGORIES = PROJECT_FILTERS.filter((f) => f.slug !== "tous");

const EMPTY_PROJECT = {
  title: "", city: "", category: "renovation", surface: "", work_types: "", cover_image: "",
  description: "", context: "", client_need: "", works: "", constraints: "", solutions: "",
  gallery_before: "", gallery_during: "", gallery_after: "", featured: false, example: false,
};

const parseList = (value) => value.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

const AdminPage = () => {
  useSEO("Administration — BÂTI 360", "Espace d'administration BÂTI 360.");
  const [user, setUser] = useState(undefined);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PROJECT);

  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  const loadQuotes = () => api.get("/admin/quote-requests").then((r) => setQuotes(r.data || [])).catch(() => {});

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) {
      loadProjects();
      loadQuotes();
    }
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/auth/login", loginForm);
      setUser(res.data);
      toast.success("Connexion réussie.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...EMPTY_PROJECT,
      ...p,
      gallery_before: (p.gallery_before || []).join("\n"),
      gallery_during: (p.gallery_during || []).join("\n"),
      gallery_after: (p.gallery_after || []).join("\n"),
    });
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const saveProject = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      gallery_before: parseList(form.gallery_before),
      gallery_during: parseList(form.gallery_during),
      gallery_after: parseList(form.gallery_after),
    };
    try {
      if (editing) {
        await api.put(`/admin/projects/${editing}`, payload);
        toast.success("Projet mis à jour.");
      } else {
        await api.post("/admin/projects", payload);
        toast.success("Projet créé.");
      }
      setEditing(null);
      setForm(EMPTY_PROJECT);
      loadProjects();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Supprimer définitivement ce projet ?")) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Projet supprimé.");
      loadProjects();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user === undefined) {
    return <div className="flex min-h-screen items-center justify-center"><div className="h-10 w-10 animate-pulse bg-bati-blue" /></div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bati-mist px-5">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm border border-black/10 bg-white p-8">
          <img src="/logo-full.webp" alt="BÂTI 360" className="mb-6 h-14 w-auto" />
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-bati-ink">Espace administrateur</h1>
          <p className="mt-1 text-sm text-bati-ink/55">Gestion des réalisations et des demandes de devis.</p>
          <div className="mt-6 space-y-4">
            <input type="email" required placeholder="E-mail" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className={INPUT} data-testid="admin-email-input" />
            <input type="password" required placeholder="Mot de passe" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className={INPUT} data-testid="admin-password-input" />
          </div>
          <button type="submit" data-testid="admin-login-submit" className="mt-6 w-full bg-bati-blue py-3 text-[13px] font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-bati-sky">
            Se connecter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bati-mist pb-20 pt-10">
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-bati-ink">Administration</h1>
            <p className="text-sm text-bati-ink/55">Connecté : {user.email}</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 border border-black/15 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-bati-ink transition-colors hover:border-bati-red hover:text-bati-red">
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>

        <div className="mt-8 flex gap-1 border-b border-black/10">
          <button onClick={() => setTab("projects")} data-testid="admin-tab-projects" className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-[0.1em] transition-colors ${tab === "projects" ? "border-b-2 border-bati-blue text-bati-blue" : "text-bati-ink/50 hover:text-bati-blue"}`}>
            <FolderKanban className="h-4 w-4" /> Réalisations ({projects.length})
          </button>
          <button onClick={() => setTab("quotes")} data-testid="admin-tab-quotes" className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-[0.1em] transition-colors ${tab === "quotes" ? "border-b-2 border-bati-blue text-bati-blue" : "text-bati-ink/50 hover:text-bati-blue"}`}>
            <Inbox className="h-4 w-4" /> Demandes ({quotes.length})
          </button>
        </div>

        {tab === "projects" && (
          <div className="mt-8 grid gap-8 lg:grid-cols-[400px_1fr]">
            <form onSubmit={saveProject} data-testid="project-form" className="h-fit border border-black/10 bg-white p-6">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-bati-ink">
                <Plus className="h-5 w-5 text-bati-sky" /> {editing ? "Modifier le projet" : "Nouveau chantier"}
              </h2>
              <div className="mt-5 space-y-4">
                <input required placeholder="Titre *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={INPUT} data-testid="project-input-title" />
                <div className="grid grid-cols-2 gap-3">
                  <input required placeholder="Ville *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={INPUT} data-testid="project-input-city" />
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={INPUT} data-testid="project-select-category">
                    {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Surface (ex. 120 m²)" value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} className={INPUT} />
                  <div className="flex items-center gap-5">
                    <label className="flex items-center gap-2 text-sm text-bati-ink/70">
                      <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#002F6C]" data-testid="project-input-featured" />
                      À la une
                    </label>
                    <label className="flex items-center gap-2 text-sm text-bati-ink/70">
                      <input type="checkbox" checked={form.example} onChange={(e) => setForm({ ...form, example: e.target.checked })} className="h-4 w-4 accent-[#002F6C]" data-testid="project-input-example" />
                      Illustration
                    </label>
                  </div>
                </div>
                <input placeholder="Nature des travaux" value={form.work_types} onChange={(e) => setForm({ ...form, work_types: e.target.value })} className={INPUT} />
                <input placeholder="URL photo principale" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={INPUT} data-testid="project-input-cover" />
                <textarea rows={2} placeholder="Description courte" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={INPUT} />
                <textarea rows={2} placeholder="Contexte" value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className={INPUT} />
                <textarea rows={2} placeholder="Besoin du client" value={form.client_need} onChange={(e) => setForm({ ...form, client_need: e.target.value })} className={INPUT} />
                <textarea rows={2} placeholder="Travaux réalisés" value={form.works} onChange={(e) => setForm({ ...form, works: e.target.value })} className={INPUT} />
                <textarea rows={2} placeholder="Contraintes techniques" value={form.constraints} onChange={(e) => setForm({ ...form, constraints: e.target.value })} className={INPUT} />
                <textarea rows={2} placeholder="Solutions apportées" value={form.solutions} onChange={(e) => setForm({ ...form, solutions: e.target.value })} className={INPUT} />
                <textarea rows={2} placeholder="Galerie AVANT — une URL d'image par ligne" value={form.gallery_before} onChange={(e) => setForm({ ...form, gallery_before: e.target.value })} className={INPUT} />
                <textarea rows={2} placeholder="Galerie CHANTIER — une URL par ligne" value={form.gallery_during} onChange={(e) => setForm({ ...form, gallery_during: e.target.value })} className={INPUT} />
                <textarea rows={2} placeholder="Galerie APRÈS — une URL par ligne" value={form.gallery_after} onChange={(e) => setForm({ ...form, gallery_after: e.target.value })} className={INPUT} />
              </div>
              <div className="mt-5 flex gap-3">
                <button type="submit" data-testid="save-project-button" className="flex-1 bg-bati-blue py-3 text-[12px] font-bold uppercase tracking-[0.1em] text-white transition-colors hover:bg-bati-sky">
                  {editing ? "Mettre à jour" : "Publier le projet"}
                </button>
                {editing && (
                  <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_PROJECT); }} className="border border-black/15 px-4 text-[12px] font-bold uppercase tracking-[0.1em] text-bati-ink/60">
                    Annuler
                  </button>
                )}
              </div>
            </form>

            <div className="space-y-3" data-testid="admin-projects-list">
              {projects.map((p) => (
                <div key={p.id} className="flex items-center gap-4 border border-black/10 bg-white p-4">
                  {p.cover_image && <img src={p.cover_image} alt="" className="h-14 w-20 shrink-0 object-cover" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-sm font-bold text-bati-ink">{p.title}</p>
                    <p className="text-xs text-bati-ink/50">{p.city} — {p.category}{p.featured ? " — À la une" : ""}</p>
                  </div>
                  <button onClick={() => startEdit(p)} data-testid={`edit-project-${p.id}`} className="grid h-9 w-9 place-items-center border border-black/10 text-bati-ink/60 transition-colors hover:border-bati-sky hover:text-bati-sky" aria-label="Modifier">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => deleteProject(p.id)} data-testid={`delete-project-${p.id}`} className="grid h-9 w-9 place-items-center border border-black/10 text-bati-ink/60 transition-colors hover:border-bati-red hover:text-bati-red" aria-label="Supprimer">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {projects.length === 0 && <p className="py-10 text-center text-sm text-bati-ink/45">Aucun projet pour le moment.</p>}
            </div>
          </div>
        )}

        {tab === "quotes" && (
          <div className="mt-8 space-y-4" data-testid="admin-quotes-list">
            {quotes.map((q) => (
              <div key={q.id} className="border border-black/10 bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-bold text-bati-ink">{q.prenom} {q.nom}</p>
                    <p className="text-sm text-bati-ink/60">{q.type_projet || "Projet"} — {q.ville || "Ville non précisée"}</p>
                  </div>
                  <div className="text-right text-xs text-bati-ink/50">
                    <p>{new Date(q.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                    <p className={q.email_sent ? "text-bati-green" : "text-bati-red"}>{q.email_sent ? "E-mail envoyé" : "E-mail non envoyé"}</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 text-sm text-bati-ink/70 sm:grid-cols-2 lg:grid-cols-3">
                  <p><span className="font-semibold text-bati-ink">Tél :</span> <a href={`tel:${q.telephone}`} className="text-bati-blue">{q.telephone}</a></p>
                  <p><span className="font-semibold text-bati-ink">E-mail :</span> <a href={`mailto:${q.email}`} className="text-bati-blue">{q.email}</a></p>
                  <p><span className="font-semibold text-bati-ink">Adresse :</span> {q.adresse || "—"} {q.ville}</p>
                  <p><span className="font-semibold text-bati-ink">Surface :</span> {q.surface || "—"}</p>
                  <p><span className="font-semibold text-bati-ink">Budget :</span> {q.budget || "—"}</p>
                  <p><span className="font-semibold text-bati-ink">Démarrage :</span> {q.date_demarrage || "—"}</p>
                </div>
                {q.description && <p className="mt-4 border-t border-black/10 pt-4 text-sm leading-relaxed text-bati-ink/70">{q.description}</p>}
                {(q.photos?.length > 0 || q.plans?.length > 0) && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {[...(q.photos || []), ...(q.plans || [])].map((f) => (
                      <a key={f} href={`${process.env.REACT_APP_BACKEND_URL}${f}`} target="_blank" rel="noopener noreferrer" className="border border-black/10 px-3 py-1.5 text-xs text-bati-blue transition-colors hover:border-bati-sky">
                        {f.split("/").pop()}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {quotes.length === 0 && <p className="py-10 text-center text-sm text-bati-ink/45">Aucune demande de devis pour le moment.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
